import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TRACK_EVENT_CATEGORY_ROUTE_STOP } from "../constants";
import { setSelectedVehicles, setStation, trackEvent } from "../model/actions";
import AppPropTypes from "../model/propTypes";
import {
  getHoursAndMinutes,
  getStationFromStop,
  getStationStatus,
} from "../utils";
import isBusEVLine from "../utils/isBusEVLine";

import RouteStopProgress from "./RouteStopProgress";
import StationIcons from "./StationIcons";

import "./RouteStop.scss";

const propTypes = {
  idx: PropTypes.number.isRequired,
  lineInfos: AppPropTypes.lineInfos.isRequired,
  openNotification: PropTypes.func,
};

function RouteStop({ idx, lineInfos, openNotification }) {
  const stations = useSelector((state) => state.stations);
  const dispatch = useDispatch();
  const currentStop = useRef();
  const stop = lineInfos.stations[idx];
  const [status, setStatus] = useState(
    lineInfos.stationsWithStatus[idx].status,
  );
  const cancelClassName = status.isCancelled ? " rt-route-stop-cancelled" : "";
  const isNextStopClassName = status.isNextStop ? " rt-route-next-stop" : "";
  const station = getStationFromStop(stations, stop);
  const { arrivalTime, departureTime, stationName } = stop;

  useEffect(() => {
    let interval = null;
    setStatus(getStationStatus(lineInfos, idx, station));

    // To see the progress bar we have to update the status of the  vehicle until we get the new stopSeqeunce.
    if (status.isInBetween) {
      interval = setInterval(() => {
        setStatus(getStationStatus(lineInfos, idx, station));
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [idx, lineInfos, status.isInBetween, stationName, station]);

  const onStopClick = useCallback(
    (e) => {
      if (e.target.classList.contains("notification-icon")) {
        openNotification?.();
        document.getElementById("route-info").scrollIntoView();
        return;
      }

      if (!station) {
        return;
      }
      dispatch(setStation(station));
      dispatch(setSelectedVehicles([]));
      dispatch(
        trackEvent(
          TRACK_EVENT_CATEGORY_ROUTE_STOP,
          "selectStation",
          station.get("name"),
        ),
      );
    },
    [station, dispatch, openNotification],
  );

  // We deactivate elevator and accessibility icons for bus EV lines
  if (station && isBusEVLine(lineInfos.line)) {
    station.set("hasElevator", false);
    station.set("hasAccessibility", false);
  }

  return (
    // We don't use a button here because some icon inside this div also use button.
    <div
      className={`rt-route-station${isNextStopClassName}${cancelClassName}`}
      onClick={onStopClick}
      onKeyPress={(evt) => evt.which === 13 && onStopClick()}
      ref={(node) => {
        if (status.isInBetween && node && node !== currentStop.current) {
          currentStop.current = node;
          node.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }}
      role="button"
      tabIndex="0"
    >
      <div className="rt-route-times">
        <div>
          {!status.isPassed &&
            !status.isLeft &&
            !status.isFirst &&
            getHoursAndMinutes(arrivalTime)}
        </div>
        <div>
          {!status.isPassed &&
            status.isFirst &&
            getHoursAndMinutes(departureTime)}
        </div>
      </div>
      <RouteStopProgress lineInfos={lineInfos} {...status} />
      <div className="rt-route-stop-name">
        <div>{stationName}</div>

        {station && (
          <StationIcons
            {...station.getProperties()}
            trackEventCategory={TRACK_EVENT_CATEGORY_ROUTE_STOP}
          />
        )}
        {/* <StationTransfers
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...properties}
        /> */}
      </div>
    </div>
  );
}

RouteStop.propTypes = propTypes;

export default memo(RouteStop);
